import type { GameView, PublicUser } from "@gierki/shared";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import { Runda } from "./Round.tsx";
import { Sprzeciwy } from "./Objections.tsx";
import { Glosowanie } from "./Voting.tsx";
import { Wyniki } from "./Results.tsx";

/**
 * Państwa-Miasta — wybór ekranu wedle fazy rundy.
 *
 * Serwer przysyła gotowy widok po każdej zmianie stanu, więc tutaj nic się nie
 * wylicza; jest tylko rozdzielnik i lobby.
 */

export interface PmView {
  phase: "lobby" | "round" | "objections" | "voting" | "results" | "finished";
  roundNo: number;
  roundCount: number;
  letter: string | null;
  categories: Array<{ key: string; label: string }>;
  settings: { endMode: string; roundSeconds: number };
  hostId: string;
  stoppedBy: string | null;
  ready: string[];
  totals: Record<string, number>;
  myAnswers: Record<string, string>;
  progress: Array<{ playerId: string; filled: number; total: number }>;
  evaluations: Array<{
    playerId: string;
    category: string;
    display: string;
    status: string;
    points: number;
  }>;
  objections: Record<string, string[]>;
  votes: Record<string, Record<string, boolean>>;
  challengeable: string[];
  history: Array<{ roundNo: number; letter: string; points: Record<string, number> }>;
  canStart: boolean;
}

/** Nagłówek zależny od fazy — trafia do paska tytułowego ekranu. */
export function pmNaglowek(pm: PmView): string {
  if (pm.phase === "lobby") return "Poczekalnia";
  return `Runda ${pm.roundNo}/${pm.roundCount}${pm.letter ? ` · litera ${pm.letter}` : ""}`;
}

export function PanstwaMiasta({
  view,
  pm,
  nicki,
  mojeId,
  wykonaj,
  onWyjdz,
  onWroc,
}: {
  view: GameView;
  pm: PmView;
  nicki: Record<string, string>;
  mojeId: string;
  wykonaj: (action: unknown) => void;
  onWyjdz: () => void;
  onWroc: () => void;
}) {
  switch (pm.phase) {
    case "lobby":
      return (
        <Lobby
          view={view}
          pm={pm}
          nicki={nicki}
          onStart={() => wykonaj({ type: "startRound" })}
          onWyjdz={onWyjdz}
        />
      );

    case "round":
      return (
        <Runda
          gameId={view.id}
          pm={pm}
          nicki={nicki}
          mojeId={mojeId}
          onStop={() => wykonaj({ type: "stop" })}
          onGotowe={() => wykonaj({ type: "ready" })}
        />
      );

    case "objections":
      return (
        <Sprzeciwy
          pm={pm}
          nicki={nicki}
          mojeId={mojeId}
          onSprzeciw={(key, on) => wykonaj({ type: "object", key, on })}
          onGotowe={() => wykonaj({ type: "ready" })}
        />
      );

    case "voting":
      return (
        <Glosowanie
          pm={pm}
          nicki={nicki}
          mojeId={mojeId}
          onGlos={(key, accept) => wykonaj({ type: "vote", key, accept })}
          onWerdykt={(key, accept) => wykonaj({ type: "verdict", key, accept })}
          onGotowe={() => wykonaj({ type: "ready" })}
        />
      );

    default:
      return (
        <Wyniki
          pm={pm}
          nicki={nicki}
          onNastepna={() => wykonaj({ type: "startRound" })}
          onWroc={onWroc}
        />
      );
  }
}

function Lobby({
  view,
  pm,
  nicki,
  onStart,
  onWyjdz,
}: {
  view: GameView;
  pm: PmView;
  nicki: Record<string, string>;
  onStart: () => void;
  onWyjdz: () => void;
}) {
  const zaMalo = view.players.length < 2;

  return (
    <>
      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
        <div className="space-y-2">
          {view.players.map((player: PublicUser) => (
            <div key={player.id} className="flex items-center justify-between">
              <span>{nicki[player.id] ?? player.nick}</span>
              <div className="flex gap-2">
                {player.id === pm.hostId ? <Odznaka>host</Odznaka> : null}
                {view.connected.includes(player.id) ? (
                  <Odznaka aktywna>online</Odznaka>
                ) : (
                  <Odznaka>offline</Odznaka>
                )}
              </div>
            </div>
          ))}
        </div>
      </Karta>

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Zasady</div>
        <div className="flex flex-wrap gap-2">
          {pm.categories.map((kategoria) => (
            <Odznaka key={kategoria.key}>{kategoria.label}</Odznaka>
          ))}
        </div>
        <div className="text-sm text-tekst-slaby mt-3">
          {pm.roundCount} rund po {Math.floor(pm.settings.roundSeconds / 60)}:
          {String(pm.settings.roundSeconds % 60).padStart(2, "0")}
          {pm.settings.endMode === "first-stop" ? " · pierwszy STOP kończy rundę" : ""}
        </div>
        {/*
          Atrybucja wymagana licencją CC BY 4.0 danych GeoNames, z których
          pochodzą miasta. Stoi w poczekalni, a nie na ekranie rundy — tam
          liczy się każda linia, a warunek licencji spełnia widoczność
          w aplikacji, nie w każdym kadrze.
        */}
        <div className="text-xs text-tekst-slaby mt-2">
          Słownik: GeoNames (CC BY 4.0), Wikidata (CC0)
        </div>
      </Karta>

      {zaMalo ? <Komunikat>Czekamy na drugą osobę — w pojedynkę nie ma z kim grać.</Komunikat> : null}

      {pm.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart} disabled={zaMalo}>
          Zaczynamy
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie partię.</Komunikat>
      )}

      <Przycisk wariant="cichy" pelna onClick={onWyjdz}>
        Wyjdź z partii
      </Przycisk>
    </>
  );
}
